import { useMemo } from "react";
import type { VisualSeed, VisualNode } from "@/hooks/useMapVisualData";
import { ROLE_COLORS, ROLE_LABELS } from "./constants";
import { RoleLegend } from "./RoleLegend";
import { Button } from "@/components/ui/button";
import { Download, FileText, Layers } from "lucide-react";

interface StatusGridProps {
  seeds: VisualSeed[];
  nodes: VisualNode[];
  onNodeClick?: (nodeId: string) => void;
}

type RowStatus = "published" | "ready" | "planned";

const STATUS_DOT: Record<RowStatus, string> = {
  published: "bg-emerald-500",
  ready: "bg-sky-500",
  planned: "bg-zinc-600",
};

const STATUS_LABEL: Record<RowStatus, string> = {
  published: "Opublikowany",
  ready: "Gotowa treść",
  planned: "Planowany",
};

// Krótkie polskie role labels (overrride dla wąskiego widoku Status Grid).
// Bazujemy na ROLE_LABELS z constants.ts ale przycinamy długie nazwy
// (troubleshooting → "Problemy") + dodajemy pillar jako "Filar".
const SHORT_ROLE_LABELS: Record<string, string> = {
  how_to: "How-to",
  definition: "Definicja",
  comparison: "Porównanie",
  history: "Historia",
  troubleshooting: "Problemy",
  guide: "Poradnik",
  glossary: "Słownik",
  commercial: "Komerc.",
  protocol: "Protokół",
  pillar: "Filar",
};

const SEED_PALETTE = [
  "#7c8cf8",
  "#5bd6a6",
  "#f2a65a",
  "#e0729b",
  "#62b6e8",
  "#b98ae0",
  "#4fcfbe",
  "#ee8c77",
  "#9fd356",
  "#facc15",
];

function getNodeStatus(n: VisualNode): RowStatus {
  const stage = n.engine_stage || "pending";
  const isPillar = n.document_role === "pillar";
  if (stage === "published") return "published";
  // Pillar pages (hub klastra) w stage "ready" = ich stan końcowy.
  // Pillar często nie przechodzi przez "published" osobno (tworzony w fazie SRO Structure),
  // więc traktujemy ready pillar jako completed dla potrzeb liczników "opublikowane".
  if (isPillar && stage === "ready") return "published";
  if (stage === "ready") return "ready";
  return "planned";
}

function isPillarNode(n: VisualNode): boolean {
  return n.document_role === "pillar";
}

function wordsOf(n: VisualNode): number {
  if (n.word_count && n.word_count > 0) return n.word_count;
  const html = n.final_html || "";
  if (!html) return 0;
  return html
    .replace(/<[^>]+>/g, " ")
    .split(/\s+/)
    .filter(Boolean).length;
}

function formatThousands(n: number): string {
  if (!n) return "0";
  if (n >= 1000) return `${Math.round(n / 1000)} tys.`;
  return String(n);
}

export function StatusGrid({ seeds, nodes, onNodeClick }: StatusGridProps) {
  const safeSeeds = useMemo(() => seeds || [], [seeds]);
  const safeNodes = useMemo(() => nodes || [], [nodes]);

  const seedSections = useMemo(() => {
    const rank: Record<RowStatus, number> = {
      published: 0,
      ready: 1,
      planned: 2,
    };
    return safeSeeds.map((seed, idx) => {
      const seedNodes = safeNodes.filter((n) => n.seed_id === seed.id);
      const counts = {
        total: seedNodes.length,
        published: 0,
        ready: 0,
        planned: 0,
        pillar: 0,
      };
      let totalWords = 0;
      for (const n of seedNodes) {
        const s = getNodeStatus(n);
        counts[s]++;
        if (isPillarNode(n)) counts.pillar++;
        totalWords += wordsOf(n);
      }
      const sortedNodes = [...seedNodes].sort((a, b) => {
        // Pillar pages na samej górze klastra (hub klastra)
        const pa = isPillarNode(a) ? 0 : 1;
        const pb = isPillarNode(b) ? 0 : 1;
        if (pa !== pb) return pa - pb;
        const sa = getNodeStatus(a);
        const sb = getNodeStatus(b);
        if (rank[sa] !== rank[sb]) return rank[sa] - rank[sb];
        const qa = a.quality_score ?? 0;
        const qb = b.quality_score ?? 0;
        if (qa !== qb) return qb - qa;
        return wordsOf(b) - wordsOf(a);
      });
      return {
        seed,
        nodes: sortedNodes,
        counts,
        totalWords,
        color: SEED_PALETTE[idx % SEED_PALETTE.length],
      };
    });
  }, [safeSeeds, safeNodes]);

  const totalsRow = useMemo(() => {
    let published = 0;
    let ready = 0;
    let planned = 0;
    let totalWords = 0;
    for (const sec of seedSections) {
      published += sec.counts.published;
      ready += sec.counts.ready;
      planned += sec.counts.planned;
      totalWords += sec.totalWords;
    }
    return {
      total: safeNodes.length,
      published,
      ready,
      planned,
      totalWords,
    };
  }, [seedSections, safeNodes.length]);

  // Pillar stats — hub klastra, faza SRO Structure. Liczone osobno bo
  // pillar pages mogą być w stage "ready" jako stan końcowy (nie zawsze
  // przechodzą przez "published" osobno). Daje user'owi widoczność że X
  // filarów istnieje + ile z nich faktycznie na żywej WP.
  const pillarStats = useMemo(() => {
    let total = 0;
    let published = 0;
    let ready = 0;
    for (const n of safeNodes) {
      if (!isPillarNode(n)) continue;
      total++;
      const stage = n.engine_stage || "pending";
      if (stage === "published") published++;
      else if (stage === "ready") ready++;
    }
    return { total, published, ready };
  }, [safeNodes]);

  const exportCsv = () => {
    const header = [
      "Seed",
      "Total",
      "Published",
      "Ready",
      "Planned",
      "Pillar",
      "Words",
    ].join(",");
    const rows = seedSections.map((s) =>
      [
        `"${(s.seed.name || "").replace(/"/g, '""')}"`,
        s.counts.total,
        s.counts.published,
        s.counts.ready,
        s.counts.planned,
        s.counts.pillar,
        s.totalWords,
      ].join(","),
    );
    const csv = [header, ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "status-grid.csv";
    a.click();
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <RoleLegend />
        <Button variant="outline" size="sm" onClick={exportCsv}>
          <Download className="h-3.5 w-3.5 mr-1" /> CSV
        </Button>
      </div>

      <div id="status-grid-top" />

      {/* Sticky cluster nav — pigułki klastrów (bez -mx-6 żeby nie wystawał poza max-w cap) */}
      {seedSections.length > 0 && (
        <nav className="sticky top-0 z-20 px-3 py-2.5 bg-background/85 backdrop-blur-md border-y border-border/40 rounded-md">
          <div className="flex items-center gap-2 overflow-x-auto pb-1">
            <a
              href="#status-grid-top"
              className="flex-shrink-0 inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full bg-card border border-violet-500/40 text-foreground hover:border-violet-500 transition-colors whitespace-nowrap"
            >
              <span className="w-2 h-2 rounded-full bg-violet-500" />
              Wszystkie
              <span className="text-muted-foreground font-medium">
                {totalsRow.total}
              </span>
            </a>
            {seedSections.map(({ seed, counts, color }) => (
              <a
                key={seed.id}
                href={`#sg-seed-${seed.id}`}
                className="flex-shrink-0 inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full bg-card/80 border border-border text-muted-foreground hover:border-foreground/40 hover:text-foreground transition-colors whitespace-nowrap"
              >
                <span
                  className="w-2 h-2 rounded-full"
                  style={{ backgroundColor: color }}
                />
                <span className="max-w-[220px] truncate">
                  {seed.name || "Klaster"}
                </span>
                <span className="text-muted-foreground/70 font-medium">
                  {counts.total}
                </span>
              </a>
            ))}
          </div>
        </nav>
      )}

      {/* Summary KPI row */}
      {seedSections.length > 0 && (
        <div className="space-y-2.5">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2.5">
            <KpiPill
              label="Klastry"
              value={seedSections.length}
              accent="violet"
            />
            <KpiPill label="Artykuły" value={totalsRow.total} />
            <KpiPill
              label="Opublikowane"
              value={totalsRow.published}
              accent="emerald"
            />
            <KpiPill
              label="Z treścią"
              value={totalsRow.ready + totalsRow.published}
              accent="sky"
            />
            <KpiPill
              label="Słowa"
              value={
                totalsRow.totalWords > 0
                  ? formatThousands(totalsRow.totalWords)
                  : "—"
              }
            />
          </div>

          {/* Pillar info banner — pokazuje pillar pages osobno (hub klastra) */}
          {pillarStats.total > 0 && (
            <div className="rounded-lg border border-violet-500/30 bg-violet-500/5 px-3.5 py-2.5 flex items-center gap-3 flex-wrap">
              <div className="flex items-center gap-2 flex-shrink-0">
                <Layers className="h-4 w-4 text-violet-400" />
                <span className="text-xs font-semibold uppercase tracking-wider text-violet-300">
                  Filary
                </span>
              </div>
              <span className="text-sm text-violet-100/90 font-medium tabular-nums">
                {pillarStats.published + pillarStats.ready}/{pillarStats.total}
              </span>
              <div className="flex items-center gap-3 ml-auto flex-wrap text-[11px] text-violet-300/80">
                {pillarStats.published > 0 && (
                  <span>
                    <span className="text-emerald-400 font-medium">
                      {pillarStats.published}
                    </span>{" "}
                    opublikowanych
                  </span>
                )}
                {pillarStats.ready > 0 && (
                  <span>
                    <span className="text-sky-400 font-medium">
                      {pillarStats.ready}
                    </span>{" "}
                    gotowych do publikacji
                  </span>
                )}
                {pillarStats.total -
                  pillarStats.published -
                  pillarStats.ready >
                  0 && (
                  <span>
                    <span className="text-zinc-400 font-medium">
                      {pillarStats.total -
                        pillarStats.published -
                        pillarStats.ready}
                    </span>{" "}
                    w produkcji
                  </span>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {seedSections.length === 0 ? (
        <div className="border-2 border-dashed rounded-xl p-12 text-center">
          <FileText className="h-10 w-10 mx-auto mb-3 text-muted-foreground/30" />
          <p className="text-muted-foreground">Brak seedów do wyświetlenia</p>
        </div>
      ) : (
        <div className="space-y-8">
          {seedSections.map(
            ({ seed, nodes: sNodes, counts, totalWords, color }) => (
              <SeedSection
                key={seed.id}
                seed={seed}
                color={color}
                nodes={sNodes}
                counts={counts}
                totalWords={totalWords}
                onNodeClick={onNodeClick}
              />
            ),
          )}
        </div>
      )}
    </div>
  );
}

function KpiPill({
  label,
  value,
  accent,
}: {
  label: string;
  value: string | number;
  accent?: "violet" | "emerald" | "sky";
}) {
  const valColor =
    accent === "violet"
      ? "text-violet-400"
      : accent === "emerald"
        ? "text-emerald-400"
        : accent === "sky"
          ? "text-sky-400"
          : "text-foreground";
  return (
    <div className="rounded-lg border bg-card/60 px-3.5 py-2.5">
      <div className={`text-lg font-bold leading-tight ${valColor}`}>
        {value}
      </div>
      <div className="mt-0.5 text-[10px] uppercase tracking-wider text-muted-foreground font-medium">
        {label}
      </div>
    </div>
  );
}

function SeedSection({
  seed,
  color,
  nodes,
  counts,
  totalWords,
  onNodeClick,
}: {
  seed: VisualSeed;
  color: string;
  nodes: VisualNode[];
  counts: {
    total: number;
    published: number;
    ready: number;
    planned: number;
    pillar: number;
  };
  totalWords: number;
  onNodeClick?: (nodeId: string) => void;
}) {
  const total = counts.total || 0;
  const pubPct = total ? (counts.published / total) * 100 : 0;
  const readyPct = total ? (counts.ready / total) * 100 : 0;
  const planPct = total ? Math.max(0, 100 - pubPct - readyPct) : 0;
  const pillarLabel =
    counts.pillar === 1 ? "filar" : counts.pillar < 5 ? "filary" : "filarów";

  return (
    <section id={`sg-seed-${seed.id}`} className="scroll-mt-24">
      <div className="flex items-center gap-3 pb-3 border-b border-border/40">
        <span
          className="w-3.5 h-3.5 rounded flex-shrink-0"
          style={{ backgroundColor: color }}
        />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="text-base lg:text-lg font-bold truncate">
              {seed.name || "Klaster"}
            </h3>
            {counts.pillar > 0 && (
              <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-violet-500/20 text-violet-300 border border-violet-500/40 inline-flex items-center gap-1 whitespace-nowrap flex-shrink-0">
                <Layers className="h-2.5 w-2.5" />
                {counts.pillar} {pillarLabel}
              </span>
            )}
          </div>
          <p className="text-[11.5px] text-muted-foreground mt-0.5">
            {total} {total === 1 ? "artykuł" : "artykułów"}
            {counts.published > 0 &&
              ` · ${counts.published} opublikowanych`}
            {counts.ready > 0 && ` · ${counts.ready} z gotową treścią`}
            {totalWords > 0 && ` · ~${formatThousands(totalWords)} słów`}
          </p>
          {total > 0 && (
            <div className="mt-1.5 flex h-1 rounded-full overflow-hidden max-w-[280px] bg-muted">
              {pubPct > 0 && (
                <div
                  className="bg-emerald-500"
                  style={{ width: `${pubPct}%` }}
                />
              )}
              {readyPct > 0 && (
                <div className="bg-sky-500" style={{ width: `${readyPct}%` }} />
              )}
              {planPct > 0 && (
                <div
                  className="bg-muted-foreground/30"
                  style={{ width: `${planPct}%` }}
                />
              )}
            </div>
          )}
        </div>
        <div
          className="text-3xl lg:text-4xl font-bold opacity-50 leading-none tabular-nums"
          style={{ color }}
        >
          {total}
        </div>
      </div>

      {nodes.length === 0 ? (
        <div className="mt-4 border border-dashed rounded-lg p-6 text-center">
          <p className="text-xs text-muted-foreground">
            Brak artykułów w tym klastrze
          </p>
        </div>
      ) : (
        <div className="mt-3 rounded-lg border bg-card/40 divide-y divide-border/40">
          {nodes.map((n) => (
            <ArticleRow
              key={n.id}
              node={n}
              onClick={onNodeClick}
            />
          ))}
        </div>
      )}
    </section>
  );
}

function ArticleRow({
  node,
  onClick,
}: {
  node: VisualNode;
  onClick?: (id: string) => void;
}) {
  const status = getNodeStatus(node);
  const role = node.document_role || "";
  const roleLabel =
    SHORT_ROLE_LABELS[role] || ROLE_LABELS[role] || (role ? role : "");
  const roleColor = ROLE_COLORS[role];
  const isPillar = role === "pillar";
  const wc = wordsOf(node);
  const qa = node.quality_score;
  const clickable = !!onClick;

  return (
    <div
      role={clickable ? "button" : undefined}
      tabIndex={clickable ? 0 : undefined}
      onClick={clickable ? () => onClick?.(node.id) : undefined}
      onKeyDown={(e) => {
        if (!clickable) return;
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onClick?.(node.id);
        }
      }}
      className={`flex items-center gap-3 px-3.5 py-2.5 hover:bg-muted/30 transition-colors ${clickable ? "cursor-pointer" : ""}`}
    >
      <span
        className={`w-2 h-2 rounded-full flex-shrink-0 ${STATUS_DOT[status]}`}
        title={STATUS_LABEL[status]}
      />
      <span className="flex-1 min-w-0 text-[13px] truncate">
        {node.title || "(bez tytułu)"}
      </span>
      {/* Fixed-width meta columns — gwarantuje wyrównanie kolumn niezależnie od długości badge'a */}
      <div className="flex items-center gap-3 flex-shrink-0">
        <span className="w-[64px] text-right text-[11px] text-muted-foreground tabular-nums">
          {wc > 0 ? `${wc} sł.` : ""}
        </span>
        <span
          className={`w-[52px] text-right text-[11px] tabular-nums font-medium ${
            qa == null
              ? "text-muted-foreground"
              : qa >= 70
                ? "text-emerald-400"
                : qa >= 40
                  ? "text-amber-400"
                  : "text-rose-400"
          }`}
        >
          {qa != null ? `QA ${qa}` : ""}
        </span>
        <div className="w-[96px] flex justify-end">
          {isPillar ? (
            <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-violet-500/20 text-violet-300 border border-violet-500/40 inline-flex items-center gap-1 whitespace-nowrap">
              <Layers className="h-2.5 w-2.5" />
              Filar
            </span>
          ) : roleLabel ? (
            <span
              className="text-[10px] font-medium px-1.5 py-0.5 rounded border whitespace-nowrap"
              style={{
                borderColor: `${roleColor || "#666"}40`,
                backgroundColor: `${roleColor || "#666"}15`,
                color: roleColor || "#aaa",
              }}
            >
              {roleLabel}
            </span>
          ) : null}
        </div>
      </div>
    </div>
  );
}
